import React from 'react';

import { useIntl } from 'react-intl-next';
import { di } from 'react-magnetic-di';

import LockIcon from '@atlaskit/icon/glyph/lock';
import { fg } from '@atlaskit/platform-feature-flags';
import { Box, Inline, Stack, Text } from '@atlaskit/primitives';
import Skeleton from '@atlaskit/skeleton';
import { Card as SmartCard, Provider as SmartCardProvider } from '@atlaskit/smart-card';
import { useQuery } from '@atlassian/conversation-assistant-service';

import { smartLinkFallback } from '../../../../../common/ui/smart-card';
import { useKnowledgeFilterService } from '../../../../../controllers/knowledge-filter';

import messages from './messages';

type KnowledgeSectionProps = {
	cloudUrl: string;
	spaceKeys: string[];
	aris?: string[];
	isForgeAgent: boolean;
};

export const AgentKnowledgeSection = ({
	cloudUrl,
	spaceKeys,
	aris,
	isForgeAgent,
}: KnowledgeSectionProps) => {
	di(useKnowledgeFilterService);
	const { formatMessage } = useIntl();
	const { getEntitiesByAris } = useKnowledgeFilterService();

	const { isFetching, data: urls } = useQuery({
		queryKey: ['getEntitiesByAris'].concat(aris || []).concat(spaceKeys || []),
		queryFn: async () => {
			if (!fg('rovo_chat_use_new_knowledge_filters')) {
				return spaceKeys.map((spaceKey) => `${cloudUrl}/wiki/spaces/${spaceKey}/overview`);
			}

			if (!aris?.length) {
				return [];
			}

			const entities = await getEntitiesByAris({
				aris,
			});
			const links = entities.map((entity) => entity.url);

			return links;
		},
		enabled: !isForgeAgent,
	});

	const getKnowledgeRender = () => {
		if (isForgeAgent) {
			return (
				<Inline space="space.100">
					<LockIcon label="" />
					<Stack space="space.100">
						<Text weight="bold">{formatMessage(messages.privateKnowledgeTitle)}</Text>
						<Text size="small">{formatMessage(messages.privateKnowledgeDescription)}</Text>
					</Stack>
				</Inline>
			);
		}

		if (!urls?.length && !isFetching) {
			return formatMessage(messages.noKnowledgeAdded);
		}

		return (
			<Stack space="space.050">
				{isFetching ? (
					<>
						<Skeleton height={22} width={200} borderRadius={8} isShimmering />
						<Skeleton height={22} width={150} borderRadius={8} isShimmering />
						<Skeleton height={22} width={200} borderRadius={8} isShimmering />
					</>
				) : (
					urls?.map((url: string) => (
						<SmartCard
							key={url}
							fallbackComponent={smartLinkFallback(url)}
							appearance="inline"
							url={url}
						/>
					))
				)}
			</Stack>
		);
	};

	return (
		<SmartCardProvider>
			<Box testId="view-agent-knowledge-cards">{getKnowledgeRender()}</Box>
		</SmartCardProvider>
	);
};
